import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Minus,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  BookOpen,
  List,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { useHelpPanel } from "../../contexts/HelpPanelContext";
import { getGuideById } from "./data";
import { ContentBlock } from "./components/ContentBlocks";

const PANEL_WIDTH = 380;
const PANEL_MIN_HEIGHT = 200;
const HEADER_HEIGHT = 42;

export default function FloatingHelpPanel() {
  const { open, guideId, sectionId, closePanel } = useHelpPanel();
  const [minimized, setMinimized] = useState(false);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [showTOC, setShowTOC] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [panelHeight, setPanelHeight] = useState(520);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const panelRef = useRef(null);
  const contentRef = useRef(null);

  const guide = guideId ? getGuideById(guideId) : null;

  // Reset section index when guide/section changes
  useEffect(() => {
    if (!guide) return;
    if (sectionId) {
      const idx = guide.sections.findIndex((s) => s.id === sectionId);
      setActiveSectionIdx(idx >= 0 ? idx : 0);
    } else {
      setActiveSectionIdx(0);
    }
    setMinimized(false);
    setShowTOC(false);
  }, [guide, sectionId]);

  // Default position: bottom-right
  useEffect(() => {
    if (open && position.x === null) {
      setPosition({
        x: window.innerWidth - PANEL_WIDTH - 24,
        y: window.innerHeight - panelHeight - 24,
      });
    }
  }, [open]);

  // Drag handler
  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX - (position.x ?? 0);
      const startY = e.clientY - (position.y ?? 0);

      const onMove = (ev) => {
        const x = Math.max(
          0,
          Math.min(window.innerWidth - PANEL_WIDTH, ev.clientX - startX),
        );
        const y = Math.max(
          0,
          Math.min(window.innerHeight - HEADER_HEIGHT, ev.clientY - startY),
        );
        setPosition({ x, y });
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [position],
  );

  // Resize handler (drag from top edge)
  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      const startY = e.clientY;
      const startH = panelHeight;
      const startTop = position.y ?? 0;

      const onMove = (ev) => {
        const delta = startY - ev.clientY;
        const newH = Math.max(
          PANEL_MIN_HEIGHT,
          Math.min(window.innerHeight - 48, startH + delta),
        );
        setPanelHeight(newH);
        setPosition((p) => ({ ...p, y: startTop - (newH - startH) }));
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [panelHeight, position],
  );

  // Scroll content to top when section changes
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [activeSectionIdx]);

  if (!open || !guide) return null;

  const section = guide.sections[activeSectionIdx];
  const totalSections = guide.sections.length;
  const hasPrev = activeSectionIdx > 0;
  const hasNext = activeSectionIdx < totalSections - 1;

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: position.x ?? undefined,
        top: position.y ?? undefined,
        width: PANEL_WIDTH,
        height: minimized ? HEADER_HEIGHT : panelHeight,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        borderRadius: minimized ? 10 : 12,
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)",
        border: `1px solid ${colors.border}`,
        background: colors.bgCard,
        transition: "height 0.2s ease",
        fontFamily: fonts.body,
      }}
    >
      {/* Resize handle (top edge) */}
      {!minimized && (
        <div
          ref={resizeRef}
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute",
            top: 0,
            left: 20,
            right: 20,
            height: 4,
            cursor: "ns-resize",
            zIndex: 2,
          }}
        />
      )}

      {/* Header — draggable */}
      <div
        ref={dragRef}
        onMouseDown={handleDragStart}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 12px",
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          background: colors.accent,
          color: "#fff",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        <GripVertical size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
        <BookOpen size={14} style={{ flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {guide.title}
        </span>

        {/* TOC toggle */}
        <button
          onClick={() => setShowTOC((v) => !v)}
          title="Table of Contents"
          style={headerBtnStyle}
        >
          <List size={14} />
        </button>
        {/* Minimize / Maximize */}
        <button
          onClick={() => setMinimized((v) => !v)}
          title={minimized ? "Expand" : "Minimize"}
          style={headerBtnStyle}
        >
          {minimized ? <Maximize2 size={14} /> : <Minus size={14} />}
        </button>
        {/* Close */}
        <button onClick={closePanel} title="Close" style={headerBtnStyle}>
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      {!minimized && (
        <>
          {/* TOC dropdown */}
          {showTOC && (
            <div
              style={{
                borderBottom: `1px solid ${colors.border}`,
                maxHeight: 240,
                overflowY: "auto",
                background: colors.bg,
              }}
            >
              {guide.sections.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveSectionIdx(i);
                    setShowTOC(false);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 16px",
                    fontSize: 12,
                    fontWeight: i === activeSectionIdx ? 700 : 400,
                    fontFamily: fonts.body,
                    color:
                      i === activeSectionIdx
                        ? colors.accent
                        : colors.textSecondary,
                    background:
                      i === activeSectionIdx ? colors.accentDim : "transparent",
                    border: "none",
                    borderBottom: `1px solid ${colors.borderLight}`,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.5, marginRight: 8 }}>{i + 1}.</span>
                  {s.title}
                </button>
              ))}
            </div>
          )}

          {/* Section content */}
          <div
            ref={contentRef}
            style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}
          >
            {/* Section heading */}
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 4,
                }}
              >
                Section {activeSectionIdx + 1} of {totalSections}
              </div>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  margin: 0,
                  color: colors.text,
                }}
              >
                {section.title}
              </h3>
            </div>

            {/* Content blocks — reusing the same renderers */}
            {section.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}
          </div>

          {/* Footer nav */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              borderTop: `1px solid ${colors.border}`,
              background: colors.bg,
            }}
          >
            <button
              onClick={() => hasPrev && setActiveSectionIdx((i) => i - 1)}
              disabled={!hasPrev}
              style={footerBtnStyle(hasPrev)}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span style={{ fontSize: 11, color: colors.textSecondary }}>
              {activeSectionIdx + 1} / {totalSections}
            </span>
            <button
              onClick={() => hasNext && setActiveSectionIdx((i) => i + 1)}
              disabled={!hasNext}
              style={footerBtnStyle(hasNext)}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const headerBtnStyle = {
  background: "rgba(255,255,255,0.12)",
  border: "none",
  borderRadius: 4,
  padding: 4,
  cursor: "pointer",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const footerBtnStyle = (enabled) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "5px 10px",
  borderRadius: 6,
  border: `1px solid ${enabled ? colors.border : "transparent"}`,
  background: enabled ? colors.bgCard : "transparent",
  color: enabled ? colors.text : colors.textSecondary,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: fonts.body,
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.4,
});
